import { useCallback } from 'react';

import { alpha, Box, Grid, makeStyles } from '@material-ui/core';

import NewMessagesContainer from 'components/NewMessagesContainer';
import { useAppDispatch, useAppSelector } from 'store';
import { AtendimentoInterfaceV2 } from 'store/reducers/atendimentos';
import { ContatoInterfaceV2 } from 'store/reducers/contatos';
import {
  fetchMensagensFinalizadasByAtendimento,
  mensagensSelector,
  setAfterCursorAConsumir,
  setBeforeCursorAConsumir,
} from 'store/reducers/mensagens';

const useStyles = makeStyles(({ palette }) => ({
  mainGrid: {
    height: '100%',
    padding: 0,
    margin: 0,
    backgroundColor: alpha(palette.background.tertiary, 0.5),
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
}));

interface MessagesGridItemProps {
  atendimento: AtendimentoInterfaceV2;
  contato: ContatoInterfaceV2;
  initialLoadingMessages?: boolean;
  onReset?: () => void;
}

export default function MessagesGridItem({
  atendimento,
  contato,
  initialLoadingMessages = true,
  onReset,
}: MessagesGridItemProps) {
  const classes = useStyles();

  const dispatch = useAppDispatch();
  const {
    historicoCursorAConsumir,
    mensagensFinalizadas,
    mensagemIdPesquisada,
    mensagensFinalizadasCursor,
    loadingMensagensFinalizadas,
  } = useAppSelector(mensagensSelector);

  const fetchNextMensagensFinalizadas = useCallback(() => {
    const { afterCursor: afterAConsumir } = historicoCursorAConsumir[0] || {};
    const { afterCursor } = mensagensFinalizadasCursor;

    const atendimentoId = atendimento?.id;

    if (!atendimento) return;

    if (!mensagemIdPesquisada) {
      dispatch(
        fetchMensagensFinalizadasByAtendimento({
          atendimentoId,
          cursor: { afterCursor },
        }),
      )
        .unwrap()
        .then(data => {
          const { afterCursor } = data.cursor;

          dispatch(setAfterCursorAConsumir({ afterCursor, type: 'historico' }));
        });

      return;
    }

    if (afterAConsumir) {
      dispatch(
        fetchMensagensFinalizadasByAtendimento({
          atendimentoId,
          cursor: { afterCursor: afterAConsumir },
        }),
      )
        .unwrap()
        .then(data => {
          const { afterCursor: currentAfterCursor } = data.cursor;

          dispatch(
            setAfterCursorAConsumir({
              afterCursor: currentAfterCursor,
              type: 'historico',
            }),
          );
        });
    }
  }, [
    atendimento,
    dispatch,
    historicoCursorAConsumir,
    mensagemIdPesquisada,
    mensagensFinalizadasCursor,
  ]);

  const fetchPreviousMensagensFinalizadas = useCallback(() => {
    const { beforeCursor: beforeAConsumir } = historicoCursorAConsumir[0] || {};
    const { beforeCursor } = mensagensFinalizadasCursor;

    const atendimentoId = atendimento?.id;

    if (!atendimentoId) return;

    if (!mensagemIdPesquisada) {
      dispatch(
        fetchMensagensFinalizadasByAtendimento({
          atendimentoId,
          cursor: { beforeCursor },
        }),
      )
        .unwrap()
        .then(data => {
          const { beforeCursor } = data.cursor;

          dispatch(
            setBeforeCursorAConsumir({ beforeCursor, type: 'historico' }),
          );
        });

      return;
    }

    if (beforeAConsumir) {
      dispatch(
        fetchMensagensFinalizadasByAtendimento({
          atendimentoId,
          cursor: { beforeCursor: beforeAConsumir },
        }),
      )
        .unwrap()
        .then(data => {
          const { beforeCursor: currentBeforeCursor } = data.cursor;

          dispatch(
            setBeforeCursorAConsumir({
              beforeCursor: currentBeforeCursor,
              type: 'historico',
            }),
          );
        });
    }
  }, [
    atendimento?.id,
    dispatch,
    historicoCursorAConsumir,
    mensagemIdPesquisada,
    mensagensFinalizadasCursor,
  ]);

  return (
    <Grid item xs component={Box} className={classes.mainGrid}>
      {!initialLoadingMessages && (
        <NewMessagesContainer
          messages={mensagensFinalizadas}
          next={fetchNextMensagensFinalizadas}
          previous={fetchPreviousMensagensFinalizadas}
          hasAfterCursor={
            mensagemIdPesquisada
              ? Boolean(historicoCursorAConsumir[0]?.afterCursor)
              : Boolean(mensagensFinalizadasCursor.afterCursor)
          }
          hasBeforeCursor={
            mensagemIdPesquisada
              ? Boolean(historicoCursorAConsumir[0]?.beforeCursor)
              : Boolean(mensagensFinalizadasCursor.beforeCursor)
          }
          onResetLista={onReset}
          loading={loadingMensagensFinalizadas}
          scrollableTargetId="tabPanelConversa"
          actions={false}
          endMessage={`Este é o início da sua conversa com ${contato.name}`}
          historicoAtendimento
        />
      )}
    </Grid>
  );
}
